import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import ComicCard from "@/components/feature/ComicCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ShareDialog from "@/components/feature/ShareDialog";
import { COMICS, REGIONS, YEARS } from "@/mocks/comics";
import { formatViews, getComicById, paginate, relatedComics } from "@/lib/comics";
import { chaptersOf } from "@/lib/comics";
import { usePageMeta } from "@/hooks/usePageMeta";

const PAGE_SIZE = 4;

export default function ComicDetail() {
  const { id } = useParams();
  const comic = getComicById(id);
  const [expanded, setExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [downloadTip, setDownloadTip] = useState("");
  const [chapterPage, setChapterPage] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setExpanded(false);
    setChapterPage(1);
  }, [id]);

  usePageMeta(
    comic ? `${comic.title} - 禁漫天堂漫画详情` : "漫画不存在 - 禁漫天堂",
    comic
      ? `${comic.title}漫画资源详情：${comic.tagline}作者${comic.author}，${comic.categories.join("、")}题材，${comic.status}，共${comic.chapters}话，禁漫天堂提供章节索引与资源格式说明。`
      : "没有找到这部漫画，可能链接有误或作品已下架。",
    comic ? `${comic.title},禁漫天堂,${comic.categories.join(",")},${comic.author}` : "禁漫天堂,漫画详情",
  );

  const chapters = useMemo(() => (comic ? chaptersOf(comic) : []), [comic]);
  const chapterPaged = useMemo(() => paginate(chapters, chapterPage, PAGE_SIZE * 6), [chapters, chapterPage]);
  const related = useMemo(() => (comic ? relatedComics(comic, 4) : []), [comic]);

  useEffect(() => {
    if (!downloadTip) return undefined;
    const timer = window.setTimeout(() => setDownloadTip(""), 2400);
    return () => window.clearTimeout(timer);
  }, [downloadTip]);

  if (!comic) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-16">
          <EmptyState
            title="没有找到这部漫画"
            description="作品可能已下架，或链接中的编号有误。你可以返回首页，或前往分类页浏览其他作品。"
            suggestions={[
              { label: "返回首页", to: "/" },
              { label: "浏览分类", to: "/category/hot-blood" },
              { label: "搜索漫画", to: "/search" },
            ]}
          />
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6">
        <nav aria-label="面包屑导航" className="flex items-center flex-wrap gap-1.5 text-xs text-foreground-400">
          <Link to="/" className="hover:text-accent-300 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-600" />
          <Link to={`/category/${comic.categories.includes("恋爱") ? "romance" : "hot-blood"}`} className="hover:text-accent-300 transition-colors">
            {comic.categories[0]}漫画
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-600" />
          <span className="text-foreground-200">{comic.title}</span>
        </nav>
      </div>

      <div className="w-full px-4 md:px-6 mt-5">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6 lg:gap-8 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="rounded-lg border hairline bg-background-900/70 overflow-hidden">
              <div className="relative w-full aspect-[3/4] bg-background-800">
                <img
                  src={comic.cover}
                  alt={`《${comic.title}》漫画封面大图`}
                  title={`${comic.title} 漫画封面`}
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute top-3 left-3 h-6 px-2 rounded-md bg-accent-500 text-background-950 text-[11px] font-label flex items-center">
                  评分 {comic.score.toFixed(1)}
                </span>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDownloadTip("演示下载，实际接入待开发")}
                    className="h-10 rounded-md bg-accent-500 text-background-950 text-[13px] font-medium flex items-center justify-center gap-1.5 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-download-2-line" />
                    下载合集
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="h-10 rounded-md border hairline text-[13px] text-foreground-200 flex items-center justify-center gap-1.5 hover:border-accent-500/50 hover:text-accent-300 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line" />
                    分享
                  </button>
                </div>
                {downloadTip && (
                  <p className="mt-2.5 rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-[11px] text-accent-200 animate-fade-in">
                    <i className="ri-information-line mr-1" />
                    {downloadTip}
                  </p>
                )}
                <dl className="mt-4 space-y-2 text-xs">
                  {[
                    { k: "作者", v: comic.author },
                    { k: "题材", v: comic.categories.join(" / ") },
                    { k: "状态", v: comic.status },
                    { k: "地区", v: comic.region },
                    { k: "连载年份", v: `${comic.year}` },
                    { k: "更新时间", v: comic.updatedAt },
                  ].map((row) => (
                    <div key={row.k} className="flex items-center justify-between gap-3">
                      <dt className="text-foreground-500 shrink-0">{row.k}</dt>
                      <dd className="text-foreground-200 truncate">{row.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>

          <div className="min-w-0 flex flex-col gap-5">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="h-6 px-2 rounded text-[11px] flex items-center bg-secondary-900/60 text-secondary-200 border border-secondary-800/70"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-2xl md:text-[34px] font-bold leading-tight text-foreground-50">
                {comic.title}
              </h1>
              <p className="mt-2 text-sm text-foreground-300">{comic.tagline}</p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                <span className="flex items-center gap-1.5"><i className="ri-eye-line text-accent-400" />{formatViews(comic.views)} 阅读</span>
                <span className="flex items-center gap-1.5"><i className="ri-heart-3-line text-secondary-300" />{formatViews(comic.follows)} 人追</span>
                <span className="flex items-center gap-1.5"><i className="ri-book-read-line text-primary-300" />共 {comic.chapters} 话</span>
              </div>
            </div>

            <section aria-labelledby="intro-heading" className="rounded-lg border hairline bg-background-900/60 p-4 md:p-5">
              <h2 id="intro-heading" className="text-sm font-medium text-foreground-50 mb-2.5">作品简介</h2>
              <p className={`text-sm leading-7 text-foreground-300 ${expanded ? "" : "line-clamp-3"}`}>{comic.intro}</p>
              <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="mt-2.5 text-xs text-accent-300 hover:text-accent-200 transition-colors cursor-pointer flex items-center gap-1"
              >
                {expanded ? "收起全文" : "展开全文"}
                <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
              </button>
            </section>

            <section aria-labelledby="download-heading" className="rounded-lg border hairline bg-background-900/60 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 id="download-heading" className="text-sm font-medium text-foreground-50">下载资源</h2>
                <span className="text-[11px] text-foreground-500">示例链接 · 演示数据</span>
              </div>
              <ul className="mt-3 space-y-2">
                {comic.files.map((file) => (
                  <li
                    key={file.label}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-md border hairline bg-background-950/50 px-3 py-2.5"
                  >
                    <span className="w-8 h-8 rounded flex items-center justify-center bg-primary-800/60 text-accent-300 shrink-0">
                      <i className="ri-file-zip-line text-sm" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] text-foreground-100 truncate">{file.label}</span>
                      <span className="block text-[11px] text-foreground-500 mt-0.5">
                        {file.format} · {file.size}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setDownloadTip("演示下载，实际接入待开发")}
                      className="h-8 px-3 rounded-md bg-background-800 text-foreground-100 text-[11px] flex items-center justify-center gap-1 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
                    >
                      <i className="ri-download-line" />
                      获取
                    </button>
                  </li>
                ))}
              </ul>
              <p className="mt-2.5 text-[11px] leading-5 text-foreground-500">
                提示：当前为功能演示版本，下载入口不会触发真实文件传输，正式资源分发逻辑待接入。
              </p>
            </section>

            <section aria-labelledby="chapter-heading" className="rounded-lg border hairline bg-background-900/60 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 id="chapter-heading" className="text-sm font-medium text-foreground-50">章节目录</h2>
                <span className="text-[11px] text-foreground-500">共 {comic.chapters} 话 · 阅读页待上线</span>
              </div>
              <div className="mt-3 max-h-[320px] overflow-y-auto pr-1">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {chapterPaged.items.map((chapter) => (
                    <li key={chapter.index}>
                      <button
                        type="button"
                        onClick={() => setDownloadTip("阅读页功能演示，待接入")}
                        className="w-full flex items-center justify-between gap-2 h-9 px-2.5 rounded-md text-xs border border-transparent hover:border-background-600/70 hover:bg-background-800/60 transition-colors cursor-pointer"
                      >
                        <span className="text-foreground-200 truncate">{chapter.title}</span>
                        <span className="text-[10px] text-foreground-500 shrink-0 font-label">{chapter.date}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <Pagination page={chapterPaged.page} totalPages={chapterPaged.totalPages} onPage={setChapterPage} />
            </section>

            <section aria-labelledby="related-heading">
              <div className="flex items-end justify-between mb-3">
                <h2 id="related-heading" className="font-heading text-base md:text-lg font-semibold text-foreground-50">
                  相关推荐
                </h2>
                <Link
                  to={`/category/${comic.categories.includes("恋爱") ? "romance" : "hot-blood"}`}
                  className="text-xs text-foreground-400 hover:text-accent-300 transition-colors whitespace-nowrap"
                >
                  查看更多同题材
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {related.map((item) => (
                  <ComicCard key={item.id} comic={item} />
                ))}
              </div>
            </section>

            <div className="rounded-lg border hairline bg-background-900/50 px-4 py-3 text-[11px] text-foreground-500 leading-5">
              本站收录地区：{REGIONS.join(" / ")}；更新年份范围：{Math.min(...YEARS)} - {Math.max(...YEARS)}。
              相关作品与年份信息用于分类检索演示，实际版权归原作者与出版方所有，请支持正版阅读。
            </div>
          </div>
        </div>
      </div>

      <div className="h-12" />
      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </SiteLayout>
  );
}